import React from 'react'
import { shape, string, element } from 'prop-types'

import { capitalize } from 'lodash'

import SectionContainer from 'components/atoms/SectionContainer'
import SectionTitle from 'components/atoms/SectionTitle'
import Container from 'components/atoms/Container'
import Paper from 'components/atoms/Paper'
import Typography from 'components/atoms/Typography'
import Markdown from 'components/atoms/MarkdownContent'
import BaseSocmedIcon from 'components/atoms/BaseSocmedIcon'
import { SVGIcon } from 'components/qstrap/SVGIcon'
import MapMarkerIcon from 'components/atoms/SVGIcons/MapMarker'

import config from 'config'

const { assets_url: assetsUrl } = config

const ContactListItem = ({ type, content, icon }) => (
  <div className="scholarship-contact__list-item">
    <div className="scholarship-contact__category">
      <div className="scholarship-contact__icon">
        { icon }
      </div>
      <Typography component="div" className="scholarship-contact__type bold" variant="body">
        { capitalize(type) }
      </Typography>
      <div className="scholarship-contact__content">
        {content}
      </div>
    </div>
    <style jsx>
      {`
        .scholarship-contact {
          &__icon {
            margin-right: 16px;
          }
          &__list-item {
            margin-bottom: .5rem;
            display: flex;
          }
          &__category {
            display: flex;
            align-items: center;
          }
          &__icon {
            align-self: flex-start;
          }
        }
        :global(.scholarship-contact__type) {
          display: none;
          @media screen and (min-width: 768px) {
            display: block;
            align-self: flex-start;
            margin-top: 4px !important;
            min-width: 108px;
          }
        }
      `}
    </style>
  </div>
)

ContactListItem.propTypes = {
  type: string,
  content: element,
  icon: element,
}

const ScholarshipContacts = ({
  contacts,
}) => {
  const { title } = contacts
  const phone = contacts.phone.split(',').map(v => v.trim())
  const getContactLink = (type) => {
    switch (type) {
      case 'website':
        return (
          <a className="scholarship-contacts__link" href={contacts.website} target="_blank" rel="nofollow noopener">
            {contacts.website}
          </a>
        )
      case 'phone':
        return phone.map((v, idx) => (
          <div className="scholarship-contacts__link">
            <a href={`tel:${v}`} target="_blank" rel="nofollow noopener">
              {v}
            </a>
            {idx < phone.length - 1 && ', '}
          </div>
        ))
      case 'email':
        return (
          <a className="scholarship-contacts__link" href={`mailto:${contacts.email}`} target="_blank" rel="nofollow noopener">
            {contacts.email}
          </a>
        )
      case 'address':
        return <Markdown content={contacts.address} />
      case 'facebook':
        return (
          <a className="scholarship-contacts__link" href={`https://fb.com/${contacts.fbUsername}`} target="_blank">
            {contacts.facebook}
          </a>
        )
      case 'twitter':
        return (
          <a className="scholarship-contacts__link" href={`https://twitter.com/${contacts.twitter}`} target="_blank">
            @{contacts.twitter}
          </a>
        )
      case 'instagram':
        return (
          <a className="scholarship-contacts__link" href={`https://instagram.com/${contacts.instagram}`} target="_blank">
            @{contacts.instagram}
          </a>
        )
      default:
        return <span>{contacts[type]}</span>
    }
  }

  const getContactIcon = (type, url) => {
    switch (type) {
      case 'website':
        return <SVGIcon name="web" size="md" />
      case 'phone':
        return <SVGIcon name="phone" size="md" />
      case 'email':
        return <SVGIcon name="mail" size="md" />
      case 'address':
        return (
          <div className="scholarship-contacts__map-icon">
            <MapMarkerIcon />
            <style jsx>
              {`
                .scholarship-contacts {
                  &__map-icon {
                    :global(svg) {
                      margin: 4px;
                      width: 24px;
                      height: 24px;
                    }
                  }
                }
              `}
            </style>
          </div>
        )
      case 'facebook':
        return <BaseSocmedIcon url={url} type={type} backgroundColor="#0fc3c4"/>
      case 'twitter':
        return <BaseSocmedIcon url={url} type={type} backgroundColor="#0fc3c4"/>
      case 'instagram':
        return <BaseSocmedIcon url={url} type={type} backgroundColor="#0fc3c4"/>
      default:
        return <svg/>
    }
  }
  return (
    <div id="contacts" className="scholarship-contacts">
      <SectionContainer
        backgroundColor="#0fc3c4"
        paddingTop={120}
        paddingBottom={120}
      >
        <div className="scholarship-contacts__content">
          <Container>
            <SectionTitle title={title} color="#fff" />
            <div className="scholarship-contacts__wrapper">
              <Paper rounded shadowed>
                <div className="scholarship-contacts__list">
                  {
                    Object.keys(contacts).map((contactItem, idx) => {
                      const isContactItem = contactItem !== 'title' && contactItem !== 'fbUsername'
                      if (isContactItem) {
                        const content = getContactLink(contactItem)
                        const icon = getContactIcon(contactItem)

                        return (
                          <ContactListItem
                            type={contactItem}
                            content={content}
                            icon={icon}
                            key={`con-li-itm-${idx}`}
                          />
                        )
                      }
                      return null
                    })
                  }
                </div>
              </Paper>
            </div>
          </Container>
        </div>
      </SectionContainer>
      <style jsx>
        {`
          .scholarship-contacts {
            position: relative;
            &__list {
              padding: 24px;
            }
            &__content {
              z-index: 2;
            }
            &__wrapper {
              max-width: 464px;
            }
            :global(&__link) {
              color: inherit;
              font-size: 16px;
              display: flex;
              align-items: center;
              height: 100%;
              overflow-wrap: anywhere;
              :global(a) {
                color: inherit !important;
              }
            }
            :global(.campus-section) {
              background-image: url('${assetsUrl}/assets/img/maps-bg-x.png');
              background-position: center center;
              background-size: cover;
            }
            :global(svg.icon) {
              color: #0fc3c4;
              width: 24px;
              height: 24px;
              margin: 4px;
            }
          }
        `}
      </style>
    </div>
  )
}

ScholarshipContacts.propTypes = {
  contacts: shape({
    title: string,
    website: string,
    email: string,
    phone: string,
    address: string,
    facebook: string,
    fbUsername: string,
    instagram: string,
    twitter: string,
  }).isRequired,
}

export default ScholarshipContacts
